import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { PlatformKycAPI, PendingKycItem } from "../../services/doctorKyc.service";

/** Only render presigned S3 URLs — rejects javascript: and non-HTTPS origins. */
function safeViewUrl(url: string | null): string | null {
  if (!url) return null;
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

const PlatformKycReviewPage: React.FC = () => {
  const [items, setItems]               = useState<PendingKycItem[]>([]);
  const [loading, setLoading]           = useState(true);
  const [rejectId, setRejectId]         = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing]             = useState(false);

  const load = () => {
    setLoading(true);
    PlatformKycAPI.listPending()
      .then(setItems)
      .catch(() => toast.error("Failed to load pending KYC docs"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleVerify = async (item: PendingKycItem) => {
    setActing(true);
    try {
      await PlatformKycAPI.verify(item.doctor_id, item.kyc_doc_id);
      toast.success(`Verified for ${item.doctor_name}`);
      setItems(prev => prev.filter(i => i.kyc_doc_id !== item.kyc_doc_id));
    } catch {
      toast.error("Verification failed");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (item: PendingKycItem) => {
    if (!rejectReason.trim()) {
      toast.warning("Rejection reason is required");
      return;
    }
    setActing(true);
    try {
      await PlatformKycAPI.reject(item.doctor_id, item.kyc_doc_id, rejectReason.trim());
      toast.success(`Rejected for ${item.doctor_name}`);
      setItems(prev => prev.filter(i => i.kyc_doc_id !== item.kyc_doc_id));
      setRejectId(null);
      setRejectReason("");
    } catch {
      toast.error("Rejection failed");
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] flex">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0">
        <div className="p-8 space-y-5">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-[#111827]">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <div>
                <h1 className="text-lg font-semibold text-[#111827]">KYC Review Queue</h1>
                <p className="text-xs text-[#6b7280]">PlatformAdmin only — internal tool, low volume.</p>
              </div>
            </div>
            {!loading && items.length > 0 && (
              <span className="bg-[#ffedd5] text-[#ea580c] text-xs font-medium px-2.5 py-1 rounded-full">
                {items.length} pending
              </span>
            )}
          </div>

          {/* Table */}
          <div className="bg-white border border-[#e2e4ea] rounded-2xl overflow-hidden overflow-x-auto">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-14 rounded-xl bg-[#f5f5f7] animate-pulse" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 flex flex-col items-center gap-3 text-center">
                <div className="h-11 w-11 rounded-full bg-white border border-[#e2e4ea] flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-[#6b7280]">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-[#111827]">No pending submissions</p>
                <p className="text-xs text-[#6b7280]">All KYC documents have been reviewed.</p>
              </div>
            ) : (
              <>
                {/* Table header */}
                <div className="bg-[#f5f5f7] flex items-center justify-between px-6 py-3 text-xs font-medium text-[#6b7280]">
                  <div className="flex items-center">
                    <span className="w-72">Clinic / Doctor</span>
                    <span className="w-36">Submitted</span>
                    <span className="w-36">Documents</span>
                  </div>
                  <span>Actions</span>
                </div>

                {/* Table rows */}
                <div className="px-6 divide-y divide-[#f3f4f6]">
                  {items.map(item => (
                    <div key={item.kyc_doc_id} className="flex items-center justify-between py-3.5">
                      {/* Left columns */}
                      <div className="flex items-center">
                        <div className="w-72">
                          <p className="text-sm font-semibold text-[#111827]">{item.clinic_name}</p>
                          <p className="text-xs text-[#6b7280] mt-0.5">{item.doctor_name}</p>
                        </div>
                        <p className="w-36 text-sm text-[#111827]">
                          {new Date(item.submitted_at).toLocaleDateString("en-IN", {
                            day: "numeric", month: "short", year: "numeric",
                          })}
                        </p>
                        <div className="w-36">
                          {safeViewUrl(item.view_url) ? (
                            <a
                              href={safeViewUrl(item.view_url)!}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2563eb] hover:underline"
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              View docs
                            </a>
                          ) : (
                            <span className="text-sm text-[#6b7280]">No doc URL</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {rejectId === item.kyc_doc_id ? (
                        <div className="flex flex-col gap-2 w-64">
                          <input
                            type="text"
                            placeholder="Rejection reason (required)"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            className="w-full border border-[#e2e4ea] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleReject(item)}
                              disabled={acting}
                              className="flex-1 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => { setRejectId(null); setRejectReason(""); }}
                              className="px-3 py-1.5 border border-[#e2e4ea] rounded-lg text-sm text-[#6b7280] hover:bg-[#f5f5f7] transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleVerify(item)}
                            disabled={acting}
                            className="bg-[#15803d] text-white text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-green-800 disabled:opacity-50 transition-colors"
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => { setRejectId(item.kyc_doc_id); setRejectReason(""); }}
                            disabled={acting}
                            className="bg-white border border-[#fca5a5] text-[#dc2626] text-sm font-medium px-3.5 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformKycReviewPage;
